// ✅ ONE COMPLETE FILE: ResumeImport.jsx (final merged, clean, no duplicates, no unused imports)

// Requires: npm i html2pdf.js



import React, { useRef, useState } from "react";

import { motion } from "framer-motion";

import html2pdf from "html2pdf.js";

import { useNavigate } from "react-router-dom";

import { supabase } from "../supabaseClient";

import { apiFetch } from "../utils/apiFetch";

import { API_BASE_URL } from "../config/apiBase";



import SummaryForm from "../pages/SummaryForm";

import ATCPanel from "../pages/ATCPanel";



import logo from "../assets/logo_basic.png";



// ============================================================
// API BASE
//
// Centralized in src/config/apiBase.js
// LOCAL:      http://localhost:8080
// PRODUCTION: https://api.reignitespirit.com
// ============================================================

// ------------------------------

// SkillsForm

// ------------------------------

function SkillsForm({ parsed, setParsed }) {

  const [skill, setSkill] = useState("");



  const addSkill = () => {

    if (!skill.trim()) return;

    setParsed({

      ...parsed,

      skills: [...(parsed.skills || []), skill.trim()],

    });

    setSkill("");

  };



  return (

    <div className="mt-2 p-3 rounded bg-gray-800 border border-gray-600">

      <div className="flex gap-2">

        <input

          value={skill}

          onChange={(e) => setSkill(e.target.value)}

          className="flex-1 p-2 rounded bg-gray-700"

          placeholder="Enter skill"

        />

        <button onClick={addSkill} className="px-4 bg-purple-600 rounded text-white">

          Add

        </button>

      </div>



      <div className="flex gap-2 mt-3 flex-wrap">

        {(parsed.skills || []).map((s, i) => (

          <span

            key={i}

            className="px-3 py-1 bg-gray-700 rounded-lg cursor-pointer"

            onClick={() =>

              setParsed({

                ...parsed,

                skills: (parsed.skills || []).filter((x) => x !== s),

              })

            }

          >

            {s} ✕

          </span>

        ))}

      </div>

    </div>

  );

}



// ------------------------------

// Helpers for PDF Template

// ------------------------------

const sectionTitleStyle = {

  letterSpacing: "0.14em",

  fontWeight: 800,

  fontSize: "10pt",

  color: "#111827",

};



function toBullets(text) {

  const lines = String(text || "")

    .split("\n")

    .map((l) => l.trim())

    .filter(Boolean);



  if (lines.length <= 1) {

    return <div style={{ whiteSpace: "pre-wrap" }}>{text}</div>;

  }



  return (

    <ul style={{ margin: "6px 0 0 18px" }}>

      {lines.map((l, i) => (

        <li key={i} style={{ marginBottom: 4 }}>

          {l.replace(/^[-•]\s*/, "")}

        </li>

      ))}

    </ul>

  );

}



/**

 * ✅ Key fix:

 * Title is inside keep-with-next, so if it lands at bottom,

 * html2pdf will move it to next page instead of cutting.

 */

function Section({ title, children }) {

  return (

    <section className="resume-section" style={{ marginTop: 14 }}>

      <div className="keep-with-next">

        <div className="resume-section-title" style={sectionTitleStyle}>

          {title}

        </div>

      </div>

      <div className="resume-section-body" style={{ marginTop: 8 }}>

        {children}

      </div>

    </section>

  );

}



// =====================================================

// ✅ PROFESSIONAL PDF TEMPLATE (A4, white, ATS-friendly)

// =====================================================

const ResumePDFTemplate = React.forwardRef(({ parsed }, ref) => {

  if (!parsed) return null;



  const exp = Array.isArray(parsed.experience) ? parsed.experience : [];

  const edu = Array.isArray(parsed.education) ? parsed.education : [];

  const skills = Array.isArray(parsed.skills) ? parsed.skills : [];

  const certs = Array.isArray(parsed.certification) ? parsed.certification : [];

  const projects = Array.isArray(parsed.projects) ? parsed.projects : [];

  const awards = Array.isArray(parsed.awards) ? parsed.awards : [];

  const pubs = Array.isArray(parsed.publications) ? parsed.publications : [];



  return (

    <div ref={ref} className="pdf-root">

      {/* ✅ Print/Page-break CSS (fixes half-cut section titles + keeps footer space) */}

      <style>

        {`

          .pdf-page{

            width:210mm;

            min-height:297mm;

            background:#fff;

            color:#111827;

            padding:14mm 14mm 18mm 14mm; /* ✅ extra bottom padding for footer safety */

            box-sizing:border-box;

            font-family: Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial;

            font-size:11pt;

            line-height:1.35;

          }



          .avoid-break{ break-inside: avoid; page-break-inside: avoid; }

          .resume-item{ break-inside: avoid; page-break-inside: avoid; }

          .keep-with-next{ break-after: avoid; page-break-after: avoid; }



          .resume-section-title{ padding-bottom: 4px; }

          img{ max-width:100%; }

        `}

      </style>



      <div className="pdf-page">

        {/* HEADER */}

        <div className="avoid-break" style={{ borderBottom: "2px solid #111827", paddingBottom: 10 }}>

          <div style={{ fontSize: "22pt", fontWeight: 900 }}>

            {parsed.name || "Your Name"}

          </div>



          <div style={{ marginTop: 6, fontSize: "10.5pt", color: "#374151" }}>

            {[parsed.targetTitle, parsed.location].filter(Boolean).join(" • ")}

          </div>



          <div style={{ marginTop: 6, fontSize: "10.5pt", color: "#374151" }}>

            {[parsed.email, parsed.phone].filter(Boolean).join(" • ")}

          </div>

        </div>



        {/* SUMMARY */}

        {parsed.summary?.trim() ? (

          <Section title="SUMMARY">

            <div className="avoid-break" style={{ marginTop: 6 }}>

              {parsed.summary}

            </div>

          </Section>

        ) : null}



        {/* SKILLS */}

        {skills.length ? (

          <Section title="SKILLS">

            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>

              {skills.map((s, i) => (

                <span

                  key={i}

                  className="avoid-break"

                  style={{

                    border: "1px solid #D1D5DB",

                    padding: "4px 8px",

                    borderRadius: 999,

                    fontSize: "10pt",

                  }}

                >

                  {s}

                </span>

              ))}

            </div>

          </Section>

        ) : null}



        {/* EXPERIENCE */}

        {exp.length ? (

          <Section title="WORK EXPERIENCE">

            {exp.map((e, idx) => (

              <div key={idx} className="resume-item" style={{ marginBottom: 12 }}>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>

                  <div style={{ fontWeight: 700 }}>

                    {(e.position || "").trim()}

                    {e.company ? ` — ${e.company}` : ""}

                  </div>



                  <div style={{ color: "#374151", fontSize: "10pt", whiteSpace: "nowrap" }}>

                    {[e.startDate, e.endDate].filter(Boolean).join(" – ")}

                  </div>

                </div>



                {e.description?.trim() ? (

                  <div style={{ marginTop: 6 }}>{toBullets(e.description)}</div>

                ) : null}

              </div>

            ))}

          </Section>

        ) : null}



        {/* EDUCATION */}

        {edu.length ? (

          <Section title="EDUCATION">

            {edu.map((e, idx) => (

              <div key={idx} className="resume-item" style={{ marginBottom: 10 }}>

                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>

                  <div style={{ fontWeight: 700 }}>

                    {e.degree || "Degree"}

                    {e.school ? ` — ${e.school}` : ""}

                  </div>

                  <div style={{ color: "#374151", fontSize: "10pt" }}>

                    {e.year || ""}

                  </div>

                </div>

              </div>

            ))}

          </Section>

        ) : null}



        {/* CERTIFICATIONS */}

        {certs.length ? (

          <Section title="CERTIFICATIONS">

            {certs.map((c, idx) => (

              <div key={idx} className="resume-item" style={{ marginBottom: 8 }}>

                <div style={{ fontWeight: 700 }}>{c.name || "Certification"}</div>

                <div style={{ color: "#374151", fontSize: "10pt" }}>

                  {[c.issuer, c.year].filter(Boolean).join(" • ")}

                </div>

              </div>

            ))}

          </Section>

        ) : null}



        {/* PROJECTS */}

        {projects.length ? (

          <Section title="PROJECTS">

            {projects.map((p, idx) => (

              <div key={idx} className="resume-item" style={{ marginBottom: 10 }}>

                <div style={{ fontWeight: 700 }}>{p.title || "Project"}</div>

                {p.description?.trim() ? (

                  <div style={{ marginTop: 4 }}>{toBullets(p.description)}</div>

                ) : null}

              </div>

            ))}

          </Section>

        ) : null}



        {/* AWARDS */}

        {awards.length ? (

          <Section title="AWARDS">

            {awards.map((a, idx) => (

              <div key={idx} className="resume-item" style={{ marginBottom: 8 }}>

                <div style={{ fontWeight: 700 }}>{a.title || "Award"}</div>

                <div style={{ color: "#374151", fontSize: "10pt" }}>

                  {[a.issuer, a.year].filter(Boolean).join(" • ")}

                </div>

              </div>

            ))}

          </Section>

        ) : null}



        {/* PUBLICATIONS */}

        {pubs.length ? (

          <Section title="PUBLICATIONS">

            {pubs.map((p, idx) => (

              <div key={idx} className="resume-item" style={{ marginBottom: 8 }}>

                <div style={{ fontWeight: 700 }}>{p.title || "Publication"}</div>

                <div style={{ color: "#374151", fontSize: "10pt" }}>

                  {[p.publisher, p.year].filter(Boolean).join(" • ")}

                </div>

              </div>

            ))}

          </Section>

        ) : null}



        {/* FOOTER */}

        <div className="avoid-break" style={{ marginTop: 18, fontSize: "9pt", color: "#6B7280" }}>

          Generated by Reignite.Spirit • ATS-friendly PDF

        </div>

      </div>

    </div>

  );

});



// =========================================================

//            MAIN COMPONENT STARTS HERE

// =========================================================

const darkBg = "#0f0f16";

const cardBg = "#1a1a28";



export default function ResumeImport() {

  const [file, setFile] = useState(null);

  const [parsed, setParsed] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [editingField, setEditingField] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");

  const [mobileTab, setMobileTab] = useState("editor"); // "editor" | "preview"

  const navigate = useNavigate();



  const pdfRef = useRef(null);



  const [activeData, setActiveData] = useState({

    name: true,

    email: true,

    phone: true,

    location: true,

    targetTitle: true,

    summary: true,

    experience: true,

    education: true,

    skills: true,

    certification: true,

    projects: true,

    awards: true,

    publications: true,

  });



  const handleCheckbox = (field) => setActiveData((prev) => ({ ...prev, [field]: !prev[field] }));



  // ✅ Download PDF

  const downloadResumePDF = async () => {

    if (!parsed) return alert("Please import or create a resume first.");



    const el = pdfRef.current;

    if (!el) return alert("PDF template not ready.");



    const filename = `${(parsed.name || "resume").replace(/\s+/g, "_")}.pdf`;



    const opt = {

      filename,

      image: { type: "jpeg", quality: 0.98 },

      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" }, // ✅ keep background white

      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },

      pagebreak: { mode: ["css", "legacy"] },

    };



    await html2pdf().set(opt).from(el).save();

  };



  // -------------- Upload Resume ----------------

  // ============================================================

// UPLOAD RESUME

//

// FLOW:

//

// 1. Check PDF locally

// 2. Get Supabase login token

// 3. Backend validates whether PDF is really a resume

// 4. If NOT resume -> STOP

// 5. If valid resume -> AI extraction

// ============================================================



const uploadResume = async () => {



  // ==========================================================

  // FILE REQUIRED

  // ==========================================================



  if (!file) {

    setErrorMsg(

      "Please choose a resume PDF."

    );



    return;

  }





  // ==========================================================

  // START PROCESSING

  // ==========================================================



  setIsLoading(true);



  setErrorMsg("");



  // Remove any previous resume preview while checking

  // the newly selected document.

  setParsed(null);





  try {



    // ========================================================

    // CLIENT-SIDE FILE TYPE CHECK

    // ========================================================



    const fileName =

      String(

        file.name || ""

      ).toLowerCase();





    if (

      !fileName.endsWith(

        ".pdf"

      )

    ) {

      throw new Error(

        "Only PDF resumes are supported. Please upload a PDF resume/CV."

      );

    }





    // ========================================================

    // FILE SIZE CHECK

    // MAXIMUM = 10 MB

    // ========================================================



    const maxFileSize =

      10 * 1024 * 1024;





    if (

      file.size >

      maxFileSize

    ) {

      throw new Error(

        "Resume PDF must be smaller than 10 MB."

      );

    }





    // ========================================================

    // GET CURRENT SUPABASE SESSION

    // ========================================================



    const {

      data:

        sessionData,



      error:

        sessionError,

    } =

      await supabase.auth

        .getSession();





    if (

      sessionError

    ) {

      console.error(

        "[Resume Import] Supabase session error:",

        sessionError

      );



      throw new Error(

        "Unable to verify your login session."

      );

    }





    const token =

      sessionData?.session

        ?.access_token;





    if (

      !token

    ) {

      throw new Error(

        "Your login session has expired. Please login again."

      );

    }





    // ========================================================

    // STEP 1

    //

    // VALIDATE WHETHER THIS DOCUMENT IS REALLY A RESUME

    //

    // IMPORTANT:

    // NO AI EXTRACTION IS CALLED YET.

    // ========================================================



    const validationFormData =

      new FormData();





    validationFormData.append(

      "file",

      file

    );





    console.log(

      "[Resume Import] Validating PDF..."

    );





    const validationUrl =
      `${API_BASE_URL}/api/resume/validate-upload`;

    console.log(
      "[Resume Import] Validation API:",
      validationUrl
    );

    const validationResponse =
      await fetch(
        validationUrl,

        {

          method:

            "POST",



          headers: {

            Authorization:

              `Bearer ${token}`,

          },



          body:

            validationFormData,

        }

      );





    // ========================================================

    // READ VALIDATION RESPONSE SAFELY

    // ========================================================



    const validationContentType =

      validationResponse.headers.get(

        "content-type"

      ) || "";





    let validationData =

      null;





    if (

      validationContentType.includes(

        "application/json"

      )

    ) {

      validationData =

        await validationResponse

          .json()

          .catch(

            () => null

          );

    }

    else {

      const validationText =

        await validationResponse

          .text()

          .catch(

            () => ""

          );





      validationData = {

        message:

          validationText,

      };

    }





    console.log(

      "[Resume Import] Validation status:",

      validationResponse.status

    );





    console.log(

      "[Resume Import] Validation result:",

      validationData

    );





    // ========================================================

    // LOGIN EXPIRED

    // ========================================================



    if (

      validationResponse.status ===

      401

    ) {

      throw new Error(

        "Your login session has expired. Please login again."

      );

    }





    // ========================================================

    // RESUME REJECTED

    // ========================================================



    if (

      !validationResponse.ok ||

      validationData?.isResume !==

        true

    ) {

      const validationMessage =

        validationData?.message ||

        "This document does not appear to be a resume/CV. Please upload a valid professional resume.";





      console.warn(

        "[Resume Import] Document rejected:",

        validationData

      );





      setParsed(

        null

      );





      setErrorMsg(

        validationMessage

      );





      return;

    }





    // ========================================================

    // RESUME VALIDATED SUCCESSFULLY

    // ========================================================



    console.log(

      "[Resume Import] Valid resume detected."

    );





    // ========================================================

    // STEP 2

    //

    // NOW RUN YOUR EXISTING AI EXTRACTION.

    //

    // The AI is reached ONLY after successful validation.

    // ========================================================



    const extractionFormData =

      new FormData();





    // Keep "resume" because your existing extraction endpoint

    // already uses this form field.

    extractionFormData.append(

      "resume",

      file

    );





    console.log(

      "[Resume Import] Starting AI resume extraction..."

    );





    const extractionUrl =
      `${API_BASE_URL}/api/resume/extract`;

    console.log(
      "[Resume Import] Extraction API:",
      extractionUrl
    );

    const extractionResponse =
      await fetch(
        extractionUrl,

        {

          method:

            "POST",



          headers: {

            Authorization:

              `Bearer ${token}`,

          },



          body:

            extractionFormData,

        }

      );





    // ========================================================

    // READ EXTRACTION RESPONSE SAFELY

    // ========================================================



    const extractionContentType =

      extractionResponse.headers.get(

        "content-type"

      ) || "";





    let extractedData =

      null;





    if (

      extractionContentType.includes(

        "application/json"

      )

    ) {

      extractedData =

        await extractionResponse

          .json()

          .catch(

            () => null

          );

    }

    else {

      const extractionText =

        await extractionResponse

          .text()

          .catch(

            () => ""

          );





      extractedData = {

        message:

          extractionText,

      };

    }





    console.log(

      "[Resume Import] Extraction status:",

      extractionResponse.status

    );





    console.log(

      "[Resume Import] Extraction result:",

      extractedData

    );





    // ========================================================

    // EXTRACTION FAILURE

    // ========================================================



    if (

      !extractionResponse.ok

    ) {

      throw new Error(

        extractedData?.message ||

        extractedData?.error ||

        "Failed to extract resume."

      );

    }





    // ========================================================

    // INVALID RESPONSE

    // ========================================================



    if (

      !extractedData ||

      typeof extractedData !==

        "object"

    ) {

      throw new Error(

        "The resume extractor returned an invalid response."

      );

    }





    // ========================================================

    // NORMALIZE EXTRACTED DATA

    // ========================================================



    extractedData.skills =

      Array.isArray(

        extractedData.skills

      )

        ? extractedData.skills

        : [];





    extractedData.experience =

      Array.isArray(

        extractedData.experience

      )

        ? extractedData.experience

        : [];





    extractedData.education =

      Array.isArray(

        extractedData.education

      )

        ? extractedData.education

        : [];





    extractedData.certification =

      Array.isArray(

        extractedData.certification

      )

        ? extractedData.certification

        : [];





    extractedData.projects =

      Array.isArray(

        extractedData.projects

      )

        ? extractedData.projects

        : [];





    extractedData.awards =

      Array.isArray(

        extractedData.awards

      )

        ? extractedData.awards

        : [];





    extractedData.publications =

      Array.isArray(

        extractedData.publications

      )

        ? extractedData.publications

        : [];





    // ========================================================

    // SUCCESS

    // ========================================================



    setParsed(

      extractedData

    );





    setErrorMsg(

      ""

    );





    console.log(

      "[Resume Import] Resume extraction completed successfully."

    );

  }

  catch (

    err

  ) {

    console.error(

      "[Resume Import] Error:",

      err

    );





    setParsed(

      null

    );





    setErrorMsg(

      err?.message ||

      "Unable to process this resume."

    );

  }

  finally {

    setIsLoading(

      false

    );

  }

};



  // ----------- Create Empty Resume -------------

  const createNewResume = () => {

    setParsed({

      name: "",

      email: "",

      phone: "",

      location: "",

      targetTitle: "",

      summary: "",

      skills: [],

      experience: [{ company: "", position: "", startDate: "", endDate: "", description: "" }],

      education: [{ degree: "", school: "", year: "" }],

      certification: [{ name: "", issuer: "", year: "" }],

      projects: [{ title: "", description: "" }],

      awards: [{ title: "", issuer: "", year: "" }],

      publications: [{ title: "", publisher: "", year: "" }],

    });



    setEditingField("name");

  };



  // -------------- Save To Supabase ----------------

  const saveToSupabase = async () => {

    if (!parsed) return alert("Nothing to save.");



    const { data } = await supabase.auth.getSession();

    const token = data?.session?.access_token;



    if (!token) {

      alert("You are not logged in (access_token missing). Please login again.");

      return;

    }



    const toStringSafe = (v) => {

      if (v === null || v === undefined) return "";

      if (typeof v === "string") return v;

      if (Array.isArray(v)) return v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join("\n");

      if (typeof v === "object") return JSON.stringify(v);

      return String(v);

    };



    const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);



    const normalizeSkills = (v) => {

      if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);

      if (typeof v === "string") return v.split(",").map((s) => s.trim()).filter(Boolean);

      return [];

    };



    const normalizeExperience = (exp) =>

      toArray(exp).map((e) => ({

        company: toStringSafe(e?.company),

        position: toStringSafe(e?.position),

        startDate: toStringSafe(e?.startDate),

        endDate: toStringSafe(e?.endDate),

        description: toStringSafe(e?.description),

      }));



    const normalizeEducation = (edu) =>

      toArray(edu).map((e) => ({

        degree: toStringSafe(e?.degree),

        school: toStringSafe(e?.school),

        year: toStringSafe(e?.year),

      }));



    const cleanText = (v) => {

      if (v == null) return "";

      const s = String(v).trim();

      if (!s) return "";

      const bad = ["empty", "n/a", "na", "null", "undefined", "-"];

      if (bad.includes(s.toLowerCase())) return "";

      return s;

    };



    const payload = {

      fullName: cleanText(parsed.name),

      jobTitle: toStringSafe(parsed.jobTitle),

      email: toStringSafe(parsed.email),

      phone: toStringSafe(parsed.phone),

      location: toStringSafe(parsed.location),

      targetTitle: toStringSafe(parsed.targetTitle),

      summary: toStringSafe(parsed.summary),



      experience: normalizeExperience(parsed.experience),

      education: normalizeEducation(parsed.education),

      skills: normalizeSkills(parsed.skills),



      certification: toArray(parsed.certification),

      projects: toArray(parsed.projects),

      awards: toArray(parsed.awards),

      publications: toArray(parsed.publications),



      photoBase64: toStringSafe(parsed.photoBase64),

    };



    const res = await apiFetch("/api/resume/save", {

      method: "POST",

      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },

      body: JSON.stringify(payload),

    });



    const dataRes = await res.json().catch(() => ({}));

    if (res.ok) alert("Saved successfully!");

    else alert("Save failed: " + (dataRes?.message || dataRes?.error || "Unknown error"));

  };



  const handleAddItem = (key) => {

    const emptyRow = {

      experience: { company: "", position: "", startDate: "", endDate: "", description: "" },

      education: { degree: "", school: "", year: "" },

      certification: { name: "", issuer: "", year: "" },

      projects: { title: "", description: "" },

      awards: { title: "", issuer: "", year: "" },

      publications: { title: "", publisher: "", year: "" },

    };



    setParsed((prev) => ({ ...prev, [key]: [...(prev[key] || []), emptyRow[key]] }));

  };



  const handleRemoveItem = (key, index) => {

    setParsed((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== index) }));

  };



  const handleUpdateItem = (key, index, field, value) => {

    const updated = [...(parsed[key] || [])];

    updated[index] = { ...(updated[index] || {}), [field]: value };

    setParsed({ ...parsed, [key]: updated });

  };



  return (

    <div className="min-h-screen overflow-x-hidden" style={{ background: darkBg, color: "white" }}>

      <header

        className="fixed top-0 left-0 w-full h-20 flex items-center

        px-4 sm:px-8 bg-[#11111f]/90 border-b border-[#EB6AB5]/20

        backdrop-blur-lg shadow-md z-50"

      >

        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/dashboard")}>

          <img

            src={logo}

            alt="Reignite Spirit Logo"

            className="w-12 sm:w-16 h-12 sm:h-16 drop-shadow-[0_0_14px_#eb6fbd]"

          />

          <h1 className="text-lg sm:text-2xl font-semibold tracking-wide text-[#EB6AB5]">REIGNITE.SPIRIT</h1>

        </div>



        <div className="ml-auto">

          <button

            onClick={() => navigate("/dashboard")}

            className="px-3 sm:px-5 py-2 rounded-full bg-gradient-to-r from-[#EB6AB5] to-[#ff83d1]

            text-[#0F0A24] font-semibold hover:scale-105 transition shadow-lg text-sm sm:text-base"

          >

            ← Dashboard

          </button>

        </div>

      </header>

{/* ✅ Mobile Tabs */}

{/* ✅ Mobile Tabs (only on small screens) */}

<div className="lg:hidden sticky top-20 z-40 px-4 pt-3">

  <div className="grid grid-cols-2 gap-2 rounded-2xl border border-[#EB6AB5]/25 bg-[#11111f]/80 backdrop-blur p-2">

    <button

      type="button"

      onClick={() => setMobileTab("edit")}

      className={`py-2 rounded-xl font-semibold transition ${

        mobileTab === "edit"

          ? "bg-gradient-to-r from-[#EB6AB5] to-[#ff83d1] text-[#0F0A24] shadow"

          : "bg-white/5 text-white/80 hover:bg-white/10"

      }`}

    >

      ✏️ Edit

    </button>



    <button

      type="button"

      onClick={() => setMobileTab("preview")}

      className={`py-2 rounded-xl font-semibold transition ${

        mobileTab === "preview"

          ? "bg-gradient-to-r from-[#EB6AB5] to-[#ff83d1] text-[#0F0A24] shadow"

          : "bg-white/5 text-white/80 hover:bg-white/10"

      }`}

    >

      👀 Preview

    </button>

  </div>

</div>

      <div className="pt-24 sm:pt-28 px-4 sm:px-8 lg:px-10 pb-10 w-full">

        <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 lg:gap-10">

          {/* LEFT */}

<motion.div

  className={`w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 rounded-2xl shadow-xl

  ${mobileTab === "edit" ? "block" : "hidden"} lg:block`}

  style={{ background: cardBg }}

>

             <h2 className="text-2xl font-bold mb-4">Import Resume</h2>

{/* ✅ Beautiful File Picker */}

<div className="mb-4">

  <label className="text-sm text-gray-300 font-medium mb-2 block">

    Upload Resume (PDF)

  </label>



  <div className="flex items-center gap-3">

    {/* Hidden Input */}

    <input

  id="resumeFile"

  type="file"

  accept=".pdf,application/pdf"

  className="hidden"



  onChange={(e) => {



    const selectedFile =

      e.target.files?.[0] ||

      null;





    // =============================================

    // NO FILE

    // =============================================



    if (

      !selectedFile

    ) {

      setFile(

        null

      );



      setParsed(

        null

      );



      setErrorMsg(

        ""

      );



      return;

    }





    // =============================================

    // PDF ONLY

    // =============================================



    const selectedName =

      String(

        selectedFile.name ||

        ""

      ).toLowerCase();





    if (

      !selectedName.endsWith(

        ".pdf"

      )

    ) {

      setFile(

        null

      );





      setParsed(

        null

      );





      setErrorMsg(

        "Only PDF resumes are supported. Please select a PDF resume/CV."

      );





      e.target.value =

        "";





      return;

    }





    // =============================================

    // MAXIMUM 10 MB

    // =============================================



    if (

      selectedFile.size >

      10 * 1024 * 1024

    ) {

      setFile(

        null

      );





      setParsed(

        null

      );





      setErrorMsg(

        "Resume PDF must be smaller than 10 MB."

      );





      e.target.value =

        "";





      return;

    }





    // =============================================

    // ACCEPT FILE

    // =============================================



    setFile(

      selectedFile

    );





    // Clear previous extraction because this is

    // now a different document.

    setParsed(

      null

    );





    setErrorMsg(

      ""

    );

  }}

/>



    {/* Styled Button */}

    <label

      htmlFor="resumeFile"

      className="px-4 py-2 rounded-xl cursor-pointer

      bg-gradient-to-r from-pink-500 to-purple-600

      text-white font-semibold shadow-md hover:opacity-90 transition"

    >

      📄 Choose Resume

    </label>



    {/* File Name */}

    <div className="flex-1 text-sm text-gray-300 truncate">

      {file ? file.name : "No file selected"}

    </div>



    {/* Remove Button */}

    {file && (

  <button

    type="button"



    onClick={() => {



      setFile(

        null

      );





      setParsed(

        null

      );





      setErrorMsg(

        ""

      );





      const input =

        document.getElementById(

          "resumeFile"

        );





      if (

        input

      ) {

        input.value =

          "";

      }

    }}



    className="

      text-xs

      px-3

      py-2

      rounded-lg

      bg-gray-700

      hover:bg-gray-600

      text-white

      transition

    "

  >

    ✕ Remove

  </button>

)}

  </div>

</div>

           <button

  type="button"



  onClick={

    uploadResume

  }



  disabled={

    isLoading ||

    !file

  }



  className={`

    px-5

    py-3

    rounded-xl

    w-full

    font-semibold

    transition-all



    ${

      isLoading ||

      !file

        ? `

          bg-gray-700

          text-gray-400

          cursor-not-allowed

        `

        : `

          bg-pink-600

          hover:bg-pink-700

          text-white

          cursor-pointer

        `

    }

  `}

>

  {

    isLoading

      ? "🔍 Validating & Extracting..."

      : "🚀 Import & Extract"

  }

</button>

{/*&#x20;

            <button onClick={createNewResume} className="mt-3 px-5 py-3 bg-gray-700 rounded-xl hover:bg-gray-600 w-full">

              ➕ Create New Resume

            </button> */}



{errorMsg && (

  <div

    className="

      mt-4

      p-4

      rounded-xl

      border

      border-red-500/40

      bg-red-500/10

      text-red-300

      text-sm

      leading-relaxed

    "

  >

    <div

      className="

        font-bold

        text-red-300

        mb-1

      "

    >

      ⚠️ Resume not accepted

    </div>



    <div>

      {errorMsg}

    </div>



    <div

      className="

        mt-2

        text-xs

        text-red-200/70

      "

    >

      Please upload a professional resume or CV containing

      information such as work experience, education, skills

      and contact details.

    </div>

  </div>

)}

            {/* CHECKBOX + EDIT */}

            {parsed && (

              <div className="mt-6">

                {[

                  ["name", "Full Name"],

                  ["email", "Email"],

                  ["phone", "Phone"],

                  ["location", "Location"],

                  ["targetTitle", "Target Title"],

                  ["summary", "Summary"],

                  ["experience", "Work Experience"],

                  ["education", "Education"],

                  ["skills", "Skills"],

                  ["certification", "Certifications"],

                  ["projects", "Projects"],

                  ["awards", "Awards"],

                  ["publications", "Publications"],

                ].map(([key, label]) => (

                  <div key={key} className="mb-4">

                    <div className="flex items-center gap-3">

                      <input type="checkbox" checked={!!activeData[key]} onChange={() => handleCheckbox(key)} />

                      <label>{label}</label>



                      <button className="ml-auto text-blue-400 underline" onClick={() => setEditingField(key)}>

                        + Add / Edit

                      </button>

                    </div>



                    {editingField === key && (

                      <div className="mt-2 ml-6">

                        {["name", "email", "phone", "location", "targetTitle"].includes(key) && (

                          <input

                            className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                            placeholder={label}

                            value={parsed[key] || ""}

                            onChange={(e) => setParsed({ ...parsed, [key]: e.target.value })}

                          />

                        )}



                        {key === "summary" && <SummaryForm parsed={parsed} setParsed={setParsed} />}



                        {key === "experience" && (

                          <div>

                            {(parsed.experience || []).map((exp, i) => (

                              <div key={i} className="mt-4 p-4 border border-gray-700 rounded-lg">

                                <div className="flex justify-between items-center mb-2">

                                  <h4 className="font-bold text-pink-400">Experience #{i + 1}</h4>

                                  <button

                                    onClick={() => handleRemoveItem("experience", i)}

                                    className="text-red-400 text-sm underline"

                                  >

                                    ❌ Remove

                                  </button>

                                </div>



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Company"

                                  value={exp.company || ""}

                                  onChange={(e) => handleUpdateItem("experience", i, "company", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Position"

                                  value={exp.position || ""}

                                  onChange={(e) => handleUpdateItem("experience", i, "position", e.target.value)}

                                />



                                <input

                                  type="date"

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  value={exp.startDate || ""}

                                  onChange={(e) => handleUpdateItem("experience", i, "startDate", e.target.value)}

                                />



                                <input

                                  type="date"

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  value={exp.endDate || ""}

                                  onChange={(e) => handleUpdateItem("experience", i, "endDate", e.target.value)}

                                />



                                <textarea

                                  className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Role Description"

                                  value={exp.description || ""}

                                  onChange={(e) => handleUpdateItem("experience", i, "description", e.target.value)}

                                />

                              </div>

                            ))}



                            <button

                              onClick={() => handleAddItem("experience")}

                              className="mt-4 px-4 py-2 bg-purple-600 rounded text-white w-full"

                            >

                              ➕ Add More Experience

                            </button>

                          </div>

                        )}



                        {key === "education" && (

                          <div>

                            {(parsed.education || []).map((edu, i) => (

                              <div key={i} className="mt-4 p-4 border border-gray-700 rounded-lg">

                                <div className="flex justify-between items-center mb-2">

                                  <h4 className="font-bold text-pink-400">Education #{i + 1}</h4>

                                  <button

                                    onClick={() => handleRemoveItem("education", i)}

                                    className="text-red-400 text-sm underline"

                                  >

                                    ❌ Remove

                                  </button>

                                </div>



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Degree"

                                  value={edu.degree || ""}

                                  onChange={(e) => handleUpdateItem("education", i, "degree", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="School"

                                  value={edu.school || ""}

                                  onChange={(e) => handleUpdateItem("education", i, "school", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Year"

                                  value={edu.year || ""}

                                  onChange={(e) => handleUpdateItem("education", i, "year", e.target.value)}

                                />

                              </div>

                            ))}



                            <button

                              onClick={() => handleAddItem("education")}

                              className="mt-4 px-4 py-2 bg-purple-600 rounded text-white w-full"

                            >

                              ➕ Add More Education

                            </button>

                          </div>

                        )}



                        {key === "skills" && <SkillsForm parsed={parsed} setParsed={setParsed} />}



                        {key === "certification" && (

                          <div>

                            {(parsed.certification || []).map((c, i) => (

                              <div key={i} className="mt-4 p-4 border border-gray-700 rounded-lg">

                                <div className="flex justify-between items-center mb-2">

                                  <h4 className="font-bold text-pink-400">Certification #{i + 1}</h4>

                                  <button

                                    onClick={() => handleRemoveItem("certification", i)}

                                    className="text-red-400 text-sm underline"

                                  >

                                    ❌ Remove

                                  </button>

                                </div>



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Certification Name"

                                  value={c.name || ""}

                                  onChange={(e) => handleUpdateItem("certification", i, "name", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Issued By"

                                  value={c.issuer || ""}

                                  onChange={(e) => handleUpdateItem("certification", i, "issuer", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Year"

                                  value={c.year || ""}

                                  onChange={(e) => handleUpdateItem("certification", i, "year", e.target.value)}

                                />

                              </div>

                            ))}



                            <button

                              onClick={() => handleAddItem("certification")}

                              className="mt-4 px-4 py-2 bg-purple-600 rounded text-white w-full"

                            >

                              ➕ Add More Certifications

                            </button>

                          </div>

                        )}



                        {key === "projects" && (

                          <div>

                            {(parsed.projects || []).map((p, i) => (

                              <div key={i} className="mt-4 p-4 border border-gray-700 rounded-lg">

                                <div className="flex justify-between items-center mb-2">

                                  <h4 className="font-bold text-pink-400">Project #{i + 1}</h4>

                                  <button

                                    onClick={() => handleRemoveItem("projects", i)}

                                    className="text-red-400 text-sm underline"

                                  >

                                    ❌ Remove

                                  </button>

                                </div>



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Project Title"

                                  value={p.title || ""}

                                  onChange={(e) => handleUpdateItem("projects", i, "title", e.target.value)}

                                />



                                <textarea

                                  className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Project Description"

                                  value={p.description || ""}

                                  onChange={(e) => handleUpdateItem("projects", i, "description", e.target.value)}

                                />

                              </div>

                            ))}



                            <button

                              onClick={() => handleAddItem("projects")}

                              className="mt-4 px-4 py-2 bg-purple-600 rounded text-white w-full"

                            >

                              ➕ Add More Projects

                            </button>

                          </div>

                        )}



                        {key === "awards" && (

                          <div>

                            {(parsed.awards || []).map((a, i) => (

                              <div key={i} className="mt-4 p-4 border border-gray-700 rounded-lg">

                                <div className="flex justify-between items-center mb-2">

                                  <h4 className="font-bold text-pink-400">Award #{i + 1}</h4>

                                  <button

                                    onClick={() => handleRemoveItem("awards", i)}

                                    className="text-red-400 text-sm underline"

                                  >

                                    ❌ Remove

                                  </button>

                                </div>



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Award Title"

                                  value={a.title || ""}

                                  onChange={(e) => handleUpdateItem("awards", i, "title", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Issuer"

                                  value={a.issuer || ""}

                                  onChange={(e) => handleUpdateItem("awards", i, "issuer", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Year"

                                  value={a.year || ""}

                                  onChange={(e) => handleUpdateItem("awards", i, "year", e.target.value)}

                                />

                              </div>

                            ))}



                            <button

                              onClick={() => handleAddItem("awards")}

                              className="mt-4 px-4 py-2 bg-purple-600 rounded text-white w-full"

                            >

                              ➕ Add More Awards

                            </button>

                          </div>

                        )}



                        {key === "publications" && (

                          <div>

                            {(parsed.publications || []).map((p, i) => (

                              <div key={i} className="mt-4 p-4 border border-gray-700 rounded-lg">

                                <div className="flex justify-between items-center mb-2">

                                  <h4 className="font-bold text-pink-400">Publication #{i + 1}</h4>

                                  <button

                                    onClick={() => handleRemoveItem("publications", i)}

                                    className="text-red-400 text-sm underline"

                                  >

                                    ❌ Remove

                                  </button>

                                </div>



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Publication Title"

                                  value={p.title || ""}

                                  onChange={(e) => handleUpdateItem("publications", i, "title", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 mb-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Publisher"

                                  value={p.publisher || ""}

                                  onChange={(e) => handleUpdateItem("publications", i, "publisher", e.target.value)}

                                />



                                <input

                                  className="w-full p-2 rounded bg-gray-800 border border-gray-600"

                                  placeholder="Year"

                                  value={p.year || ""}

                                  onChange={(e) => handleUpdateItem("publications", i, "year", e.target.value)}

                                />

                              </div>

                            ))}



                            <button

                              onClick={() => handleAddItem("publications")}

                              className="mt-4 px-4 py-2 bg-purple-600 rounded text-white w-full"

                            >

                              ➕ Add More Publications

                            </button>

                          </div>

                        )}

                      </div>

                    )}

                  </div>

                ))}

              </div>

            )}



            <ATCPanel parsed={parsed} setParsed={setParsed} />

{/*&#x20;

            <button onClick={saveToSupabase} className="mt-6 px-6 py-3 bg-green-600 rounded-xl hover:bg-green-700 w-full">

              💾 Save Resume to Supabase

            </button> */}



            <button onClick={downloadResumePDF} className="mt-3 px-6 py-3 bg-blue-600 rounded-xl hover:bg-blue-700 w-full">

              ⬇️ Download Resume (PDF)

            </button>

          </motion.div>



          {/* RIGHT PREVIEW */}

          <motion.div

  className={`w-full lg:w-1/2 p-4 sm:p-6 lg:p-10 rounded-2xl border border-gray-700 shadow-2xl ${

    mobileTab !== "preview" ? "hidden lg:block" : ""

  }`}

  style={{ background: "linear-gradient(to bottom, #151521, #1c1c2c)" }}

>

            {!parsed ? (

              <p className="text-gray-400 text-center mt-10">Upload or create a resume to see preview.</p>

            ) : (

              <div className="max-h-[70vh] lg:max-h-[calc(100vh-180px)] overflow-y-auto pr-2">

                {activeData.name && parsed.name && <h1 className="text-2xl sm:text-3xl font-bold">{parsed.name}</h1>}

                {activeData.email && parsed.email && <p className="break-words">{parsed.email}</p>}

{activeData.phone && parsed.phone && <p className="break-words">{parsed.phone}</p>}

{activeData.location && parsed.location && <p className="break-words">{parsed.location}</p>}

                <hr className="my-6 border-gray-600" />



                {activeData.targetTitle && parsed.targetTitle && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Target Title</h2>

                    <p>{parsed.targetTitle}</p>

                  </>

                )}



                {activeData.summary && parsed.summary && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Summary</h2>

                    <p>{parsed.summary}</p>

                  </>

                )}



                {activeData.experience && (parsed.experience || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Work Experience</h2>

                    {(parsed.experience || []).map((exp, i) => (

                      <div key={i} className="p-3 mt-3 border border-gray-700 rounded-lg">

                        <strong>{exp.company}</strong> — {exp.position}

                        <br />

                        {exp.startDate} - {exp.endDate}

                        <p>{exp.description}</p>

                      </div>

                    ))}

                  </>

                )}



                {activeData.education && (parsed.education || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Education</h2>

                    {(parsed.education || []).map((edu, i) => (

                      <div key={i} className="p-3 mt-3 border border-gray-700 rounded-lg">

                        <strong>{edu.degree}</strong>

                        <br />

                        {edu.school}

                      </div>

                    ))}

                  </>

                )}



                {activeData.skills && (parsed.skills || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Skills</h2>

                    <div className="flex gap-2 flex-wrap">

                      {(parsed.skills || []).map((s, i) => (

                        <span key={i} className="px-3 py-1 bg-gray-700 rounded-lg">

                          {s}

                        </span>

                      ))}

                    </div>

                  </>

                )}



                {activeData.certification && (parsed.certification || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Certifications</h2>

                    {(parsed.certification || []).map((c, i) => (

                      <div key={i} className="p-3 mt-3 border border-gray-700 rounded-lg">

                        <strong>{c.name}</strong>

                        <br />

                        {c.issuer} — {c.year}

                      </div>

                    ))}

                  </>

                )}



                {activeData.projects && (parsed.projects || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Projects</h2>

                    {(parsed.projects || []).map((p, i) => (

                      <div key={i} className="p-3 mt-3 border border-gray-700 rounded-lg">

                        <strong>{p.title}</strong>

                        <p>{p.description}</p>

                      </div>

                    ))}

                  </>

                )}



                {activeData.awards && (parsed.awards || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Awards</h2>

                    {(parsed.awards || []).map((a, i) => (

                      <div key={i} className="p-3 mt-3 border border-gray-700 rounded-lg">

                        <strong>{a.title}</strong>

                        <br />

                        {a.issuer} — {a.year}

                      </div>

                    ))}

                  </>

                )}



                {activeData.publications && (parsed.publications || []).length > 0 && (

                  <>

                    <h2 className="text-purple-400 text-xl font-semibold mt-6">Publications</h2>

                    {(parsed.publications || []).map((p, i) => (

                      <div key={i} className="p-3 mt-3 border border-gray-700 rounded-lg">

                        <strong>{p.title}</strong>

                        <br />

                        {p.publisher} — {p.year}

                      </div>

                    ))}

                  </>

                )}

              </div>

            )}

          </motion.div>

        </div>

      </div>



      {/* ✅ HIDDEN PROFESSIONAL PDF TEMPLATE */}

      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>

        <ResumePDFTemplate ref={pdfRef} parsed={parsed} />

      </div>

    </div>

  );

}
